/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMemo, useEffect } from 'react';
import type { DealHash, Deal } from '../interface';

export type DealDrawerInfo = {
  dealIdListString: string;
  dealNameListString: string;
  dealDescriptionListString: string;
};

/**
 * Custom hook to track deal drawer information.
 * Automatically calls the tracking function when deals change.
 *
 * @param deals - Hash of deals to extract information from
 * @param trackOnDealsAvailableLink - Callback function to track deals information
 */
export const useDealDrawerTracking = (
  deals: DealHash,
  trackOnDealsAvailableLink: (dealDrawerInfo: DealDrawerInfo) => void,
): void => {
  const dealDrawerInfo = useMemo(() => {
    if (Object.keys(deals).length === 0) {
      return null;
    }

    const dealIdList: string[] = [];
    const dealNameList: Array<string | null> = [];
    const dealDescriptionList: Array<string | null> = [];

    Object.values(deals).forEach((deal: Deal) => {
      dealIdList.push(deal.id);
      dealNameList.push(deal.name);
      dealDescriptionList.push(deal.description);
    });

    return {
      dealIdListString: dealIdList.join(':'),
      dealNameListString: dealNameList.join(':'),
      dealDescriptionListString: dealDescriptionList.join(':'),
    };
  }, [deals]);

  useEffect(() => {
    if (dealDrawerInfo) {
      trackOnDealsAvailableLink(dealDrawerInfo);
    }
  }, [dealDrawerInfo, trackOnDealsAvailableLink]);
};
